/**
 * Vocabulaire d'affichage des macros — pur, sans React.
 *
 * L'ordre des quatre macros est fixé **une fois ici** et suivi partout : bandeau
 * de la journée, jauges d'un repas, formulaire de saisie, éditeur de modèle. Un
 * ordre qui change d'un écran à l'autre coûte une demi-seconde de relecture à
 * chaque coup d'œil, et c'est précisément ce que le §6.2 des specs cherche à
 * éviter.
 *
 * ⚠️ Aucun calcul de macros ici : le serveur en est seul juge (§5.1). Ce fichier
 * ne fait que nommer et mettre en forme.
 */

import { roundMacroForDisplay, type MacroSet } from '../../../shared/types';

export type MacroKey = keyof MacroSet;

export interface MacroRow {
  key: MacroKey;
  /** Libellé long — bandeau de la journée. */
  label: string;
  /** Libellé court — jauges d'un repas, où la place manque. */
  short: string;
  unit: string;
}

export const MACRO_ROWS: readonly MacroRow[] = [
  { key: 'protein', label: 'Protéines', short: 'Prot.', unit: 'g' },
  { key: 'carbs', label: 'Glucides', short: 'Gluc.', unit: 'g' },
  { key: 'fat', label: 'Lipides', short: 'Lip.', unit: 'g' },
  { key: 'kcal', label: 'Calories', short: 'kcal', unit: 'kcal' },
];

/**
 * Arrondi d'affichage : le dixième de gramme, la kcal entière.
 *
 * ⚠️ La **règle** d'arrondi vit dans `shared/types.ts` depuis l'étape 9 : le
 * bilan de fin de journée (specs §6.7) est composé côté serveur et doit porter
 * exactement les chiffres de cet écran-ci. Seule la mise en forme locale
 * (séparateur de milliers français) reste ici.
 */
export function formatMacro(value: number, unit: string): string {
  return roundMacroForDisplay(value, unit === 'kcal').toLocaleString('fr-FR');
}

/**
 * Part parcourue d'un objectif — ce que remplit un anneau de progression.
 *
 * 🔴 **Un objectif nul ne donne pas un anneau plein.** Le cas se présente pour
 * un repas ajouté à la main, qui n'a pas d'objectif propre (étape 5) : diviser
 * par zéro donnerait `Infinity`, que `ProgressRing` bornerait à 1 — un anneau
 * complet annonçant un objectif atteint qui n'existe **pas**. On renvoie donc
 * zéro, et l'anneau n'affiche que sa piste.
 *
 * ⚠️ **Non bornée en sortie.** La saturation à 100 % appartient à l'anneau, pas
 * au calcul : le dépassement doit rester lisible ailleurs — le chiffre du
 * restant, lui, n'est jamais borné (specs §6.2).
 *
 * ⚠️ Ici et pas dans `MacroBanner.tsx` : `react-refresh` interdit à un module de
 * composants d'exporter autre chose. C'est la contrainte même qui a créé
 * `components/ui/tokens.ts` à l'étape 5.
 */
export function ratioOf(consumed: number, target: number): number {
  if (!(target > 0)) return 0;
  return consumed / target;
}
